import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { EntityProps, EntityState, removeEntity, setAllEntities, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';
import { SelectedEntityState } from '../../shared/store/selected-entity.feature';
import { Sequence } from '../models/séquence.model';
import { SequenceService } from '../parcours-sequence.service';
import { SequenceCreateRequest } from '../séquence-form/requests/create-request';
import { SequenceUpdateRequest } from '../séquence-form/requests/update-request';

export function avecMéthodesSequences<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<Sequence> & SelectedEntityState>(),
      props: type<EntityProps<Sequence>>(),
    },
    withMethods((store) => ({
      chargerSequences: (state: { sequences: Sequence[] }) => patchState(store, setAllEntities(state.sequences)),
      modifierSequence: (state: { id: string; sequence: Partial<Sequence> }) =>
        patchState(store, updateEntity({ id: state.id, changes: state.sequence })),
      ajouterSequence: (state: { sequence: Sequence }) => {
        const s = store.entities().map((x) => {
          // Pour ne pas traiter les séquences désactivées
          if (x.indice === undefined || x.indice === null) return x;
          // Pour ne pas modifier les séquences qui vont se placer avant la nouvelle
          if (x.indice < state.sequence.indice!) return x;

          return {
            ...x,
            indice: x.indice + 1,
          };
        });
        s.push(state.sequence);
        return patchState(store, setAllEntities(s));
      },
      selectionnerSequence: (state: { id: string }) => patchState(store, { selectedEntityId: state.id }),
      supprimerSequence: (state: { id: string }) => patchState(store, removeEntity(state.id)),
    })),
    withMethods((store, service = inject(SequenceService)) => ({
      charger: rxMethod<string>(
        pipe(
          switchMap((idModule) =>
            service.trouverTous(idModule).pipe(
              tapResponse({
                next: (result) => store.chargerSequences({ sequences: result.items }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération de séquences, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      ajouterUneSequence: rxMethod<{
        newSequence: SequenceCreateRequest;
        next: () => void;
      }>(
        pipe(
          switchMap(({ newSequence, next }) =>
            service.ajouter(newSequence).pipe(
              tapResponse({
                next: (sequence) => {
                  store.ajouterSequence({ sequence });
                  store.selectionnerSequence({ id: sequence.id });
                  store.supprimerSequence({ id: 'null' });
                  next();
                },
                error: (err) => {
                  console.error("Error lors de l'ajout d'une sequence via le Store: ", err);
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Une erreur est survenue lors de la modification de cette séquence, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        id: string;
        data: SequenceUpdateRequest;
        next?: () => void;
      }>(
        pipe(
          switchMap(({ id, data, next }) =>
            service.modifier(id, data).pipe(
              tapResponse({
                next: () => next?.(),
                error: (err) => {
                  console.error("Error lors de la modification d'une sequence : ", err);
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Une erreur est survenue lors de la modification de cette séquence, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      selection: rxMethod<string>(
        pipe(
          switchMap((id) => service.trouveParId(id)),
          tapResponse({
            next: (séquence) => {
              store.modifierSequence({ id: séquence.id, sequence: séquence });
              store.selectionnerSequence({ id: séquence.id });
            },
            error: (err) => {
              console.error(err);
              const toastrId = uuidv7();
              toast.custom(ToastrErrorComponent, {
                componentProps: {
                  message: `Un problème est survenu lors de la récupération de la séquence, contactez le support.`,
                  id: toastrId,
                },
                id: toastrId,
              });
            },
          }),
        ),
      ),
      supprimer: rxMethod<{ idSéquence: string; next: () => void }>(
        pipe(
          switchMap(({ idSéquence, next }) =>
            service.supprimer(idSéquence).pipe(
              tapResponse({
                next: () => {
                  store.supprimerSequence({ id: idSéquence });
                  next();
                },
                error: (err) => {
                  console.error("Error lors de la suppression d'une sequence : ", err);
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Une erreur est survenue lors de la suppression de cette séquence, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      enregistrerOrdre: rxMethod<{
        id: string;
        request: SequenceUpdateRequest;
        optimisticSequences: Sequence[];
        next?: () => void;
      }>(
        pipe(
          tap(({ optimisticSequences }) => store.chargerSequences({ sequences: optimisticSequences })),
          switchMap(({ id, request, next }) =>
            service.modifier(id, request).pipe(
              tapResponse({
                next: () => {
                  next?.();
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Une erreur est survenue lors de la suppression de cette séquence, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
